import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Flag, AlertTriangle, RefreshCw, Smile, Frown, PartyPopper } from "lucide-react";

const MinesweeperGame = () => {
    const navigate = useNavigate();
    
    // Config
    const DIFFICULTY = {
        Easy: { rows: 9, cols: 9, mines: 10 },
        Medium: { rows: 16, cols: 16, mines: 40 },
        Hard: { rows: 16, cols: 30, mines: 99 } // Scrollable
    };

    const [difficulty, setDifficulty] = useState("Easy");
    const [grid, setGrid] = useState([]); // Array of { isMine, isOpen, isFlagged, count }
    const [gameStatus, setGameStatus] = useState("waiting"); // waiting, playing, lost, won
    const [mineCount, setMineCount] = useState(10);
    const [timer, setTimer] = useState(0);
    const [firstClick, setFirstClick] = useState(true);

    // Timer
    useEffect(() => {
        let interval;
        if (gameStatus === "playing") {
            interval = setInterval(() => {
                setTimer(t => t + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [gameStatus]);

    useEffect(() => {
        resetGame();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [difficulty]);

    const resetGame = () => {
        const { rows, cols, mines } = DIFFICULTY[difficulty];
        // Create empty grid
        const newGrid = Array(rows * cols).fill(null).map((_, i) => ({
            id: i,
            isMine: false,
            isOpen: false,
            isFlagged: false,
            count: 0
        }));
        
        setGrid(newGrid);
        setGameStatus("waiting");
        setMineCount(mines);
        setTimer(0);
        setFirstClick(true);
    };

    const getNeighbors = (index, r, c) => {
        const { rows, cols } = DIFFICULTY[difficulty];
        const row = Math.floor(index / cols);
        const col = index % cols;
        const neighbors = [];

        for (let i = -1; i <= 1; i++) {
            for (let j = -1; j <= 1; j++) {
                if (i === 0 && j === 0) continue;
                const newRow = row + i;
                const newCol = col + j;
                if (newRow >= 0 && newRow < rows && newCol >= 0 && newCol < cols) {
                    neighbors.push(newRow * cols + newCol);
                }
            }
        }
        return neighbors;
    };

    const placeMines = (safeIndex) => {
        const { rows, cols, mines } = DIFFICULTY[difficulty];
        const newGrid = [...grid];
        let placed = 0;
        
        while (placed < mines) {
            const idx = Math.floor(Math.random() * (rows * cols));
            // First click safety: don't place on safeIndex OR its neighbors (optional, but nicer)
            // Let's just ensure not on safeIndex for now.
            if (idx !== safeIndex && !newGrid[idx].isMine) {
                newGrid[idx].isMine = true;
                placed++;
            }
        }

        // Calculate counts
        for (let i = 0; i < newGrid.length; i++) {
            if (!newGrid[i].isMine) {
                const neighbors = getNeighbors(i);
                let count = 0;
                neighbors.forEach(nIdx => {
                    if (newGrid[nIdx].isMine) count++;
                });
                newGrid[i].count = count;
            }
        }

        return newGrid;
    };

    const handleClick = (index) => {
        if (gameStatus === "lost" || gameStatus === "won") return;
        if (grid[index].isFlagged || grid[index].isOpen) return;

        let currentGrid = [...grid];

        if (gameStatus === "waiting") {
            setGameStatus("playing");
            // Generate mines now ensuring safety
            currentGrid = placeMines(index);
        }

        if (currentGrid[index].isMine) {
            revealAllMines(currentGrid);
            setGameStatus("lost");
        } else {
            revealCell(currentGrid, index);
            checkWin(currentGrid);
        }
        setGrid(currentGrid);
    };

    const handleRightClick = (e, index) => {
        e.preventDefault();
        if (gameStatus === "lost" || gameStatus === "won") return;
        if (grid[index].isOpen) return;

        const newGrid = [...grid];
        newGrid[index].isFlagged = !newGrid[index].isFlagged;
        setGrid(newGrid);
        setMineCount(prev => newGrid[index].isFlagged ? prev - 1 : prev + 1);
    };

    const revealCell = (currentGrid, index) => {
        if (currentGrid[index].isOpen || currentGrid[index].isFlagged) return;
        
        currentGrid[index].isOpen = true;
        
        if (currentGrid[index].count === 0) {
            const neighbors = getNeighbors(index);
            neighbors.forEach(nIdx => {
                revealCell(currentGrid, nIdx);
            });
        }
    };

    const revealAllMines = (currentGrid) => {
        currentGrid.forEach(cell => {
            if (cell.isMine) cell.isOpen = true;
        });
    };

    const checkWin = (currentGrid) => {
        const { rows, cols, mines } = DIFFICULTY[difficulty];
        const hiddenNonMines = currentGrid.filter(c => !c.isMine && !c.isOpen).length;
        if (hiddenNonMines === 0) {
            setGameStatus("won");
            setMineCount(0);
            // Flag all mines visually
            currentGrid.forEach(c => {
                 if(c.isMine) c.isFlagged = true;
            });
        }
    };

    // Render Helpers
    const getCellContent = (cell) => {
        if (cell.isFlagged) return <Flag className="w-4 h-4 text-red-500 fill-red-500" />;
        if (!cell.isOpen) return null;
        if (cell.isMine) return <div className="w-4 h-4 rounded-full bg-gray-900 animate-bounce"></div>; // Bomb
        if (cell.count > 0) return <span className={`font-bold ${
            cell.count === 1 ? 'text-blue-600' :
            cell.count === 2 ? 'text-green-600' :
            cell.count === 3 ? 'text-red-600' :
            cell.count === 4 ? 'text-purple-800' :
            'text-gray-800'
        }`}>{cell.count}</span>;
        return null; // Empty 0
    };

    const { rows, cols } = DIFFICULTY[difficulty];

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col items-center py-8 px-4 overflow-hidden">
             {/* Header */}
             <div className="w-full max-w-4xl flex items-center justify-between mb-4">
                 <button
                    onClick={() => navigate("/games")}
                    className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                 >
                    <ArrowLeft className="w-5 h-5 mr-1" /> Back to Games
                 </button>
                 <h1 className="text-3xl font-bold text-gray-800">Minesweeper</h1>
                 <div className="w-16"></div>
            </div>

            <div className="bg-gray-200 border-4 border-gray-400 p-2 rounded-lg shadow-xl inline-block max-w-[100%] overflow-auto">
                {/* Game Header */}
                <div className="bg-gray-300 border-b-4 border-gray-400 p-3 mb-3 flex items-center justify-between shadow-inner">
                    {/* Mine Counter */}
                    <div className="bg-black text-red-600 font-mono text-3xl px-2 py-1 rounded border-2 border-gray-500 w-24 text-center">
                        {String(Math.max(0, mineCount)).padStart(3, '0')}
                    </div>

                    {/* Reset Button (Face) */}
                    <button
                        onClick={resetGame}
                        className="w-12 h-12 bg-gray-200 border-4 border-t-white border-l-white border-b-gray-500 border-r-gray-500 active:border-t-gray-500 active:border-l-gray-500 active:border-b-white active:border-r-white flex items-center justify-center text-2xl"
                    >
                         {gameStatus === 'lost' ? <Frown size={28} className="text-gray-800" /> : 
                          gameStatus === 'won' ? <PartyPopper size={28} className="text-gray-800" /> :
                          <Smile size={28} className="text-gray-800" />}
                    </button>

                     {/* Timer */}
                     <div className="bg-black text-red-600 font-mono text-3xl px-2 py-1 rounded border-2 border-gray-500 w-24 text-center">
                        {String(Math.min(999, timer)).padStart(3, '0')}
                    </div>
                </div>

                {/* Difficulty Selector */}
                <div className="flex justify-center gap-2 mb-4">
                    {Object.keys(DIFFICULTY).map(level => (
                         <button
                            key={level}
                            onClick={() => setDifficulty(level)}
                            className={`px-3 py-1 text-sm font-bold border-2 rounded ${
                                difficulty === level 
                                ? 'bg-blue-600 text-white border-blue-800' 
                                : 'bg-gray-200 text-gray-700 border-gray-400 hover:bg-gray-300'
                            }`}
                         >
                             {level}
                         </button>
                    ))}
                </div>

                {/* Grid */}
                <div 
                    className="grid gap-[1px] bg-gray-400 border-4 border-t-gray-500 border-l-gray-500 border-b-white border-r-white mx-auto select-none"
                    style={{ 
                        gridTemplateColumns: `repeat(${cols}, 24px)`,
                        width: 'fit-content'
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                >
                    {grid.map((cell) => (
                        <div
                            key={cell.id}
                            onClick={() => handleClick(cell.id)}
                            onContextMenu={(e) => handleRightClick(e, cell.id)}
                            className={`
                                w-6 h-6 flex items-center justify-center text-sm font-bold cursor-pointer
                                ${cell.isOpen 
                                    ? 'bg-gray-200 border border-gray-300' 
                                    : 'bg-gray-300 border-2 border-t-white border-l-white border-b-gray-500 border-r-gray-500 hover:bg-gray-250 active:border-none'
                                }
                                ${cell.isOpen && cell.isMine ? 'bg-red-500' : ''}
                            `}
                        >
                            {getCellContent(cell)}
                        </div>
                    ))}
                </div>
            </div>
            
            <p className="mt-4 text-gray-500 text-sm mb-8">
                Left click to reveal. Right click to flag.
            </p>

            {/* Rules Section */}
            <div className="max-w-md bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2 text-yellow-500" />
                    How to Play
                </h3>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                    <li>
                        <strong>Goal:</strong> Reveal all safe squares without detonating a mine.
                    </li>
                    <li>
                        <strong>Numbers:</strong> A number indicates how many mines are touching that square (horizontally, vertically, or diagonally).
                    </li>
                    <li>
                        <strong>Flagging:</strong> Right-click a square to put a flag (<Flag className="w-3 h-3 inline text-red-500" />) where you think a mine is.
                    </li>
                    <li>
                        <strong>Winning:</strong> The game is won when all non-mine squares are revealed.
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default MinesweeperGame;
